import {React, styled} from 'common'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch} from 'react-redux'
import PullToRefresh from 'react-simple-pull-to-refresh'
import Header from './Header/Header'
import {useIsRtl} from 'store/Store'
import SideMenu from './SideMenu/SideMenu'
import CssBaseline from '@mui/material/CssBaseline'
import CircularProgress from '@mui/material/CircularProgress'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import {HEADER_HEIGHT, SIDE_MENU_DURATION} from 'appearance/styles'
import usePage from 'logic/usePage'
import useResponsive from 'logic/useResponsive'
import {Colors} from 'appearance'
import {useLoader} from 'context/LoaderContext'
import PageLoader from 'components/Display/Loader/PageLoader'
import {useSideMenuContext} from 'context/SideMenuContext'
import baseApi, {API_TAGS} from 'store/slices/api/baseApi'

const PullingIndicator = ({label}) => (
    <RefreshWrapper>
        <BounceArrow />
        <RefreshText>{label}</RefreshText>
    </RefreshWrapper>
)

const RefreshingIndicator = ({label}) => (
    <RefreshWrapper>
        <CircularProgress size={28} thickness={4} />
        <RefreshText>{label}</RefreshText>
    </RefreshWrapper>
)

const PageContent = ({children, marginContentPage}) => {
    const {isLoading} = useLoader()
    const dispatch = useDispatch()
    const {mediumScreen} = useResponsive()
    const {t} = useTranslation()

    const handleRefresh = useCallback(
        () =>
            new Promise((resolve) => {
                dispatch(baseApi.util.invalidateTags(API_TAGS))
                setTimeout(resolve, 800)
            }),
        [dispatch]
    )

    return (
        <Content style={{marginInlineStart: marginContentPage}}>
            {isLoading ? (
                <PageLoader show={isLoading} />
            ) : (
                <StyledPullToRefresh
                    onRefresh={handleRefresh}
                    isPullable={mediumScreen}
                    pullDownThreshold={120}
                    maxPullDownDistance={120}
                    resistance={2}
                    backgroundColor={Colors.background}
                    pullingContent={<PullingIndicator label={t('pullToRefresh.pulling')} />}
                    refreshingContent={
                        <RefreshingIndicator label={t('pullToRefresh.refreshing')} />
                    }>
                    {children}
                </StyledPullToRefresh>
            )}
        </Content>
    )
}

const Page = ({children}) => {
    const isRtl = useIsRtl()
    const {open, setOpen} = useSideMenuContext()
    const {marginContentPage, user, account} = usePage({open})
    return (
        <Container dir={isRtl ? 'rtl' : 'ltr'}>
            <CssBaseline />
            <Header openMenu={open} onClickOpenMenu={() => setOpen(true)} />
            <SideMenu user={user} account={account} />
            <PageContent marginContentPage={marginContentPage}>{children}</PageContent>
        </Container>
    )
}

export default Page

const Container = styled.div`
    width: 100%;
    height: 100dvh;
    background-color: ${Colors.background};
`
const Content = styled.div`
    padding: 10px;
    display: flex;
    flex-direction: column;
    align-items: ${(p) => (p.noHeader ? 'center' : 'flex-start')};
    margin-inline-start: ${(p) => p.margin}px;
    margin-top: 64px;
    transition: all ${SIDE_MENU_DURATION}ms;
    height: calc(100dvh - ${HEADER_HEIGHT}px);
    overflow-y: hidden;
`

const StyledPullToRefresh = styled(PullToRefresh)`
    & .ptr__children {
        overflow-y: auto !important;
    }
`

const RefreshWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 12px 0;
`

const RefreshText = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${Colors.APP_FADED_GREY};
    letter-spacing: 0.3px;
`

const BounceArrow = styled(ArrowDownwardRoundedIcon)`
    font-size: 28px !important;
    color: ${Colors.primary};
    animation: bounceDown 1.1s ease-in-out infinite;

    @keyframes bounceDown {
        0%,
        100% {
            transform: translateY(0);
            opacity: 0.5;
        }
        50% {
            transform: translateY(6px);
            opacity: 1;
        }
    }
`
